/**
 * Timeline Service
 * API client for timeline events
 */

import { apiClient } from './api-client';
import type {
  TimelineEventType,
  TimelineQueryParams,
  TimelineResponse
} from '@/types/timeline';
import type { ApiResponse } from '@/types/tasks';

export const timelineService = {
  /**
   * Get client timeline events
   */
  async getClientTimeline(
    clientId: string,
    params?: TimelineQueryParams
  ): Promise<ApiResponse<TimelineResponse>> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    if (params?.types && params.types.length > 0) {
      queryParams.append('types', params.types.join(','));
    }

    if (params?.fromDate) queryParams.append('fromDate', params.fromDate);
    if (params?.toDate) queryParams.append('toDate', params.toDate);

    const queryString = queryParams.toString();
    const url = `/api/clients/${clientId}/timeline${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * Get timeline events with auto-pagination
   * (helper for infinite scroll)
   */
  async getTimelineWithPagination(
    clientId: string,
    page: number = 1,
    limit: number = 20,
    filters?: {
      types?: TimelineEventType[];
      fromDate?: string;
      toDate?: string;
    }
  ): Promise<TimelineResponse> {
    const response = await this.getClientTimeline(clientId, {
      page,
      limit,
      ...filters
    });

    return {
      events: response.data.events,
      pagination: response.data.pagination
    };
  }
};
